/**
 * Persistence utilities for the workflow store.
 *
 * This module provides the debounced persistGraph function and flushPersist
 * for saving flow graph state (nodes + edges) back to the API.
 *
 * Integration: Import and wire into workflow-store.ts actions:
 * - Call `persistGraph(get)` at the end of: onNodesChange, addNode, removeNode,
 *   updateNodeConfig, onConnect, updateEdgeCondition
 * - Call `flushPersist(get)` on tab switch / page unload
 *
 * This is a separate module because Plan 04 runs in a parallel worktree and
 * cannot modify workflow-store.ts directly (Plan 01 owns that file).
 * The merge step will integrate these additions.
 */

import type { Node, Edge } from '@xyflow/react'

interface FlowNodeData {
  id: string
  type: string
  label: string
  config: Record<string, unknown>
  input_schema: Array<{ name: string; type: string }>
  output_schema: Array<{ name: string; type: string }>
  variable_mappings: Array<{
    source_node_id: string
    source_output: string
    target_input: string
  }>
  error_handling: Record<string, unknown> | null
  [key: string]: unknown
}

interface StoreState {
  flowNodes: Node[]
  flowEdges: Edge[]
  currentWorkflow: { id: string; project_id: string } | null
}

type GetState = () => StoreState

// Debounce utility -- 800ms per RESEARCH recommendation
let debounceTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Transforms React Flow nodes back to API shape and persists via PATCH.
 * Debounced at 800ms to prevent excessive API calls (T-02-11 mitigation).
 */
export function persistGraph(get: GetState): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }

  debounceTimer = setTimeout(async () => {
    const { flowNodes, flowEdges, currentWorkflow } = get()
    if (!currentWorkflow) return

    const projectId = currentWorkflow.project_id
    const workflowId = currentWorkflow.id

    // Transform React Flow nodes back to API shape
    const nodes = flowNodes.map((n) => {
      const data = n.data as unknown as FlowNodeData
      return {
        ...data,
        position: n.position,
      }
    })

    const edges = flowEdges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      condition: (e.data as Record<string, unknown>)?.condition ?? null,
      branch_label: (e.data as Record<string, unknown>)?.branch_label ?? null,
    }))

    try {
      // Use the workflow service to persist -- dynamically import to avoid circular deps
      const { workflowService } = await import('@/services/workflow')
      await workflowService.update(projectId, workflowId, { nodes, edges })
    } catch (err) {
      console.error('Failed to persist graph:', err)
      // Don't toast on auto-save failures -- too noisy
    }
  }, 800)
}

/**
 * Forces immediate persistence, clearing any pending debounce.
 * Use on tab switch or page unload to ensure no data loss.
 */
export function flushPersist(get: GetState): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }

  // Run persistence synchronously (best-effort on unload)
  const { flowNodes, flowEdges, currentWorkflow } = get()
  if (!currentWorkflow) return

  const projectId = currentWorkflow.project_id
  const workflowId = currentWorkflow.id

  const nodes = flowNodes.map((n) => {
    const data = n.data as unknown as FlowNodeData
    return {
      ...data,
      position: n.position,
    }
  })

  const edges = flowEdges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    condition: (e.data as Record<string, unknown>)?.condition ?? null,
    branch_label: (e.data as Record<string, unknown>)?.branch_label ?? null,
  }))

  // Fire-and-forget -- best effort on tab switch
  import('@/services/workflow')
    .then(({ workflowService }) =>
      workflowService.update(projectId, workflowId, { nodes, edges })
    )
    .catch((err) => console.error('Failed to flush persist graph:', err))
}
